import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import curbsideAPIService from '../services/curbsideAPIService';
import { toast } from 'react-toastify';

// Fetch all templates
export const fetchTemplatesDataReducer = createAsyncThunk(
  'storeSettings/fetchTemplatesDataReducer',
  async (storeId: string | null, { rejectWithValue }) => {
    const endpoint = 'messagetemplate/all'; 
    const data = storeId ? { storeId } : {}; // Pass {} if storeId is null

    try {
      const response = await curbsideAPIService.get(endpoint, data);
      if (!Array.isArray(response)) throw new Error('Invalid data format');
      return response;
    } catch (error) {
      console.error('Error fetching templates data:', error);
      return rejectWithValue((error as any).response ?? 'Failed to fetch templates data');
    }
  }
);

// Create new template
export const handleCreateTemplate = createAsyncThunk(
  'storeSettings/handleCreateTemplate',
  async (templateData: Record<string, any>, { dispatch, rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const storeIds = state.storeSettings?.storeIds;
      await curbsideAPIService.post('messageTemplate', templateData);
      dispatch(fetchTemplatesDataReducer(storeIds[0])); // Pass a valid storeId or null
      return true;
    } catch (error) {
      return rejectWithValue((error as any).message);
    }
  }
);

// Update template
export const handleUpdateTemplate = createAsyncThunk(
  'storeSettings/handleUpdateTemplate',
  async (newTemplateData: Record<string, any>, { dispatch, rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const storeIds = state.storeSettings?.storeIds;
      await curbsideAPIService.put('messageTemplate', newTemplateData);
      dispatch(fetchTemplatesDataReducer(storeIds[0])); // Pass a valid storeId or null
      return true;
    } catch (error) {
      return rejectWithValue((error as any).message);
    }
  }
);

// Delete template
export const handleDeleteTemplate = createAsyncThunk(
  'storeSettings/handleDeleteTemplate',
  async (
    { id }: { id: string },
    { dispatch, rejectWithValue, getState }
  ) => {
    try {
      const state: any = getState();
      const storeIds = state.storeSettings?.storeIds;

      await curbsideAPIService.delete('messagetemplate', {
        messageTemplateID: id,
        storeIds: storeIds,
      });

      dispatch(fetchTemplatesDataReducer(storeIds[0]));
      return true;
    } catch (error) {
      return rejectWithValue((error as any).message ?? 'Failed to delete template');
    }
  }
);


// Fetch all link templates
export const fetchLinkTemplatesDataReducer = createAsyncThunk(
  'storeSettings/fetchLinkTemplatesDataReducer',
  async (storeId: string | null,  { rejectWithValue }) => {
    const endpoint = 'eventmessagetemplate/all';
    const data = storeId ? { storeId } : {}; // Pass {} if storeId is null

    try {

      // 

      const response = await curbsideAPIService.get(endpoint, data ); // Pass params as query parameters

      if (!Array.isArray(response)) throw new Error('Invalid data format');
      return response;
    } catch (error) {
      console.error('Error fetching link templates data:', error);
      return rejectWithValue((error as any).response ?? 'Failed to fetch link templates data');
    }
  }
);

// Fetch all integrations

export const fetchIntegrationsDataReducer = createAsyncThunk(
  'storeSettings/fetchIntegrationsDataReducer',
  async (storeId: string | null, { rejectWithValue }) => {
    const endpoint = 'integrations/all'; 
    const data = { storeId: storeId ?? null };

    try {
      const response = await curbsideAPIService.get(endpoint, data);
      if (!Array.isArray(response)) throw new Error('Invalid data format');
      return response;
    } catch (error) {
      console.error('Error fetching templates data:', error);
      return rejectWithValue((error as any).response ?? 'Failed to fetch templates data');
    }
  }
);

// Create a new link template
export const handleCreateLinkTemplate = createAsyncThunk(
  'storeSettings/handleCreateLinkTemplate',
  async (linkTemplateData: Record<string, any>, { dispatch, rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const storeIds = state.storeSettings?.storeIds;
      await curbsideAPIService.post('eventmessagetemplate/link', linkTemplateData);
      dispatch(fetchLinkTemplatesDataReducer(storeIds[0])); // Pass an empty array or the appropriate storeId(s)
      return true;
    } catch (error) {
      return rejectWithValue((error as any).message);
    }
  }
);

// Delete link template
export const handleRemoveLinkedTemplate = createAsyncThunk(
  'storeSettings/handleRemoveLinkedTemplate',
  async (
    { id }: { id: string },
    { dispatch, rejectWithValue, getState }
  ) => {
    try {
      const state: any = getState();
      const storeIds = state.storeSettings?.storeIds;

      await curbsideAPIService.delete(
        `eventmessagetemplate/unlink/${id}`,
        storeIds
      );

      dispatch(fetchLinkTemplatesDataReducer(storeIds[0]));
    } catch (error) {
      return rejectWithValue((error as any).message ?? 'Failed to unlink template');
    }
  }
);



// Fetch chain setting
export const fetchChainSetting = createAsyncThunk(
  'chainSettings/fetchChainSetting',
  async (storeIds, { rejectWithValue }) => {
    const endpoint = `chainsetting/${process.env.REACT_APP_CHAINSETTINGID}`;
    const data = { storeIds: storeIds ?? [] };

    try {
      const response = await curbsideAPIService.get(endpoint, data);
      return response;
    } catch (error) {
      console.error('Error fetching chain settings data:', error);
      return rejectWithValue((error as any).response ?? 'Failed to fetch chain settings');
    }
  }
);

// Save notification settings
export const handleSendNotification = createAsyncThunk(
  'storeSettings/handleSendNotification',
  async (chainSettingData, { rejectWithValue }) => {
    try {
      await curbsideAPIService.post('chainsetting', {
        chainSetting: chainSettingData,
      });
      toast.success('Notification setting saved successfully');
      return true;
    } catch (error) {
      return rejectWithValue((error as any).message);
    }
  }
);

export const fetchTemplateById = createAsyncThunk(
  'storeSettings/fetchTemplateById',
  async (id : string, { rejectWithValue }) => {
    const endpoint = `messagetemplate/${id}`;
    const data = {};
    try {
      const response = await curbsideAPIService.get(endpoint, data);
      return response;
    } catch (error) {
      return rejectWithValue((error as any).message);
    }
  }
);



// Slice
const storeSettingsSlice = createSlice({
  name: 'storeSettings',
  initialState: {
    messageTemplteList: [] as string[],
    isEditing: false,
    showTemplateModal: false,
    selectedTemplate: '',
    linkTemplateList: [] as string[],
    currentTemplate: { messageTemplateID: '', name: '', content: '' },
    chainSetting: {},
    selectedRecipients: [],
    selectedEventIndex: null,
    showLinkTemplateModal: false,
    phoneNumber: null,
    storeIds: [] as string[],
    status: null as string | null,
    error: null as unknown,
    linkIntegrations: [] as string[],
    currentIntegration: { name: "", value: "", isActive: false }
  },
  reducers: {
    setSelectedTemplate(state, action) {
      return {
        ...state,
        selectedTemplate: action.payload,
      };
    },
    setIntegrationList(state, action) {
      return {
        ...state,
        linkIntegrations: action.payload,
      };
    },
    setCurrentIntegration(state, action) {
      return {
        ...state,
        currentIntegration: action.payload,
      };
    },
    setPhoneNumber(state, action) {
      return {
        ...state,
        phoneNumber: action.payload,
      };
    },
    setShowLinkTemplateModal(state, action) {
      return {
        ...state,
        showLinkTemplateModal: action.payload,
      };
    },
    setSelectedEventIndex(state, action) {
      return {
        ...state,
        selectedEventIndex: action.payload,
      };
    },
    setShowTemplateModal(state, action) {
      return {
        ...state,
        showTemplateModal: action.payload,
      };
    },
    setIsEditing(state, action) {
      return {
        ...state,
        isEditing: action.payload,
      };
    },
    setCurrentTemplate(state, action) {
      return {
        ...state,
        currentTemplate: action.payload,
      };
    },
    setMessageTemplateList(state, action) {
      return {
        ...state,
        messageTemplteList: action.payload,
      };
    },
    setSelectedRecipients: (state, action) => {
      return {
        ...state,
        selectedRecipients: action.payload,
      };
    },
    setStoreIds: (state, action) => {
      state.storeIds = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTemplatesDataReducer.fulfilled, (state, action) => {
        return {
          ...state,
          messageTemplteList: action.payload,
          status: 'succeeded',
        };
      })
      .addCase(fetchTemplatesDataReducer.rejected, (state, action) => {
        return {
          ...state,
          status: 'failed',
          error: action.payload,
        };
      })
      .addCase(fetchLinkTemplatesDataReducer.fulfilled, (state, action) => {
        return {
          ...state,
          linkTemplateList: action.payload,
        };
      })
      .addCase(handleSendNotification.fulfilled, (state, action) => {
        return {
          ...state,
          autoSend: false,
          selectedOption: '',
        };
      })
      .addCase(fetchIntegrationsDataReducer.fulfilled, (state, action) => {
        return {
          ...state,
          linkIntegrations: action.payload,
        };
      })
      .addCase(fetchTemplateById.fulfilled, (state, action) => {
        return {
          ...state,
          currentTemplate: action.payload,
        };
      });
      ;
  },
});

export const {
  setSelectedRecipients,
  setShowTemplateModal,
  setIsEditing,
  setCurrentTemplate,
  setSelectedTemplate,
  setSelectedEventIndex,
  setShowLinkTemplateModal,
  setPhoneNumber,
  setStoreIds,
  setCurrentIntegration,
  setMessageTemplateList,
  setIntegrationList

} = storeSettingsSlice.actions;

export default storeSettingsSlice.reducer;